import { NEWS_SOURCES, FALLBACK_NEWS, parseRssXml } from './rssFeeds'
import { formatRelativeTimeLang, cleanTitle } from './i18nHelper'

/**
 * Hämtar nyheter från backend-API (/api/news eller /api/news.json) med fallback till CORS-proxy och offline-data.
 */
export async function fetchNews(forceRefresh = false, lang = 'en') {
  // 1. Försök anropa backend eller statisk news.json (fungerar på både server och Firebase Hosting)
  const ts = Date.now()
  const endpoints = forceRefresh 
    ? [`/api/news?refresh=true&_=${ts}`, `/api/news.json?_=${ts}`, '/api/news.json'] 
    : ['/api/news', `/api/news.json?_=${ts}`, '/api/news.json']

  for (const endpoint of endpoints) {
    try {
      const controller = new AbortController()
      const timeoutId = setTimeout(() => controller.abort(), 6000)

      const response = await fetch(endpoint, {
        signal: controller.signal,
        cache: forceRefresh ? 'no-cache' : 'default'
      })
      clearTimeout(timeoutId)

      if (response.ok) {
        const data = await response.json()
        if (Array.isArray(data) && data.length > 0) {
          return data
            .filter(item => !item.id?.includes('offline') && item.link !== 'https://www.svt.se/nyheter')
            .map(item => ({
              ...item,
              title: cleanTitle(item.title),
              timeAgo: formatRelativeTimeLang(item.pubDate, lang)
            }))
        }
      }
    } catch {
      // Prova nästa endpoint
    }
  }

  // 2. Om endpoints inte svarade, prova att hämta källorna via CORS-proxy
  try {
    const feedPromises = []

    for (const source of NEWS_SOURCES) {
      if (!source.defaultActive) continue
      const feedUrl = source.feedUrls[0]
      const proxyUrl = `https://api.allorigins.win/raw?url=${encodeURIComponent(feedUrl)}`

      feedPromises.push(
        fetch(proxyUrl, { signal: AbortSignal.timeout(6000) })
          .then(res => {
            if (!res.ok) throw new Error(`HTTP ${res.status}`)
            return res.text()
          })
          .then(xml => parseRssXml(xml, source, lang))
          .catch(err => {
            console.warn(`Kunde inte hämta ${source.name} via proxy:`, err.message)
            return []
          })
      )
    }

    const results = await Promise.all(feedPromises)
    const combined = results.flat()

    if (combined.length > 0) {
      combined.sort((a, b) => new Date(b.pubDate) - new Date(a.pubDate))
      return combined
    }
  } catch (proxyErr) {
    console.warn('Proxy-hämtning misslyckades:', proxyErr.message)
  }

  // 3. Om nätverk/proxy är otillgängligt (t.ex. offline), använd kuraterad fallback-data
  return FALLBACK_NEWS.map(item => ({
    ...item,
    timeAgo: formatRelativeTimeLang(item.pubDate, lang)
  }))
}
